import { Link } from 'react-router-dom';
import { usePageMeta } from '../../hooks/usePageMeta';
import { useI18nLocaleSync } from '../../hooks/useI18nLocaleSync';
import { useLocalizedPath } from '../../hooks/useLocalizedPath';

export function UnderstandingLotteryOdds() {
  const lp = useLocalizedPath();
  useI18nLocaleSync();
  usePageMeta({
    title: 'Understanding Lottery Odds – The Math Explained | Lotto Number Generator',
    description:
      'A plain-English explanation of how Powerball, Mega Millions, and Lotto 6/45 odds are calculated, what expected value means, and why no strategy improves your chances.',
    path: '/articles/understanding-lottery-odds',
  });

  return (
    <main className="content">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to={lp('/')}>Home</Link>
        <span className="sep">/</span>
        <Link to="/articles/">Articles</Link>
        <span className="sep">/</span>
        <span className="current">Understanding Lottery Odds</span>
      </nav>

      <article className="about-section">
        <h1>Understanding Lottery Odds: The Math Behind Powerball and Mega Millions</h1>
        <p>
          Lottery odds are among the most misunderstood numbers in everyday life. People routinely
          underestimate just how large "1 in 292 million" really is, and the lottery industry's
          marketing language—"Someone has to win!"—encourages a cognitive shortcut that makes winning
          feel inevitable. This article explains how lottery odds are calculated from first principles,
          what expected value means for a lottery ticket, and why no system or strategy can change
          your mathematical probability of winning.
        </p>

        <h2 style={{ fontSize: '1.2em', marginTop: 28, marginBottom: 8 }}>The Basics: Combinations, Not Permutations</h2>
        <p>
          Lottery draws are <strong>combination</strong> events, not permutation events. This means
          the order in which the balls are drawn does not matter—only whether your selected numbers
          match the drawn set. If you pick 1, 15, 22, 38, 52, your ticket wins regardless of whether
          the balls came out as 52, 1, 38, 22, 15 or in any other order.
        </p>
        <p>
          The number of distinct combinations of <em>k</em> items chosen from a pool of <em>n</em>{' '}
          items (without replacement, order irrelevant) is given by the binomial coefficient:
        </p>
        <p style={{ fontFamily: 'monospace', background: 'var(--input-bg)', padding: '10px 16px', borderRadius: 6, margin: '12px 0', color: 'var(--text-primary)' }}>
          C(n, k) = n! / (k! × (n − k)!)
        </p>
        <p>
          This formula is the foundation of every lottery odds calculation. Each combination is equally
          likely to be drawn, so the probability of any one specific combination winning equals 1
          divided by the total number of combinations.
        </p>

        <h2 style={{ fontSize: '1.2em', marginTop: 28, marginBottom: 8 }}>Powerball Odds: Step by Step</h2>
        <p>
          Powerball draws 5 main numbers from 1–69 and 1 Powerball from 1–26. The jackpot requires
          matching all 5 main numbers plus the Powerball.
        </p>
        <p><strong>Step 1: Count the ways to choose 5 from 69.</strong></p>
        <p style={{ fontFamily: 'monospace', background: 'var(--input-bg)', padding: '10px 16px', borderRadius: 6, margin: '12px 0', color: 'var(--text-primary)' }}>
          C(69, 5) = 69! / (5! × 64!) = 11,238,513
        </p>
        <p>
          There are 11,238,513 distinct ways to choose 5 numbers from a pool of 69. Your ticket covers
          exactly one of those combinations.
        </p>
        <p><strong>Step 2: Account for the Powerball.</strong></p>
        <p>
          The Powerball is drawn independently from a pool of 26 numbers. You need to match the one
          correct Powerball, so multiply:
        </p>
        <p style={{ fontFamily: 'monospace', background: 'var(--input-bg)', padding: '10px 16px', borderRadius: 6, margin: '12px 0', color: 'var(--text-primary)' }}>
          11,238,513 × 26 = 292,201,338
        </p>
        <p>
          Your odds of winning the Powerball jackpot with a single $2 ticket are exactly{' '}
          <strong>1 in 292,201,338</strong>.
        </p>

        <h2 style={{ fontSize: '1.2em', marginTop: 28, marginBottom: 8 }}>Mega Millions Odds</h2>
        <p>
          Mega Millions draws 5 main numbers from 1–70 and 1 Mega Ball from 1–24. The calculation
          follows the same structure:
        </p>
        <p style={{ fontFamily: 'monospace', background: 'var(--input-bg)', padding: '10px 16px', borderRadius: 6, margin: '12px 0', color: 'var(--text-primary)' }}>
          C(70, 5) × 24 = 12,103,014 × 24 = 290,472,336
        </p>
        <p>
          The Mega Millions jackpot odds are <strong>1 in 290,472,336</strong>—about 0.6% better
          than Powerball's since the April 2025 redesign, which shrank the Mega Ball pool from 25 to
          24 and raised the ticket price to $5 with a built-in multiplier.
        </p>

        <h2 style={{ fontSize: '1.2em', marginTop: 28, marginBottom: 8 }}>Korean Lotto 6/45 Odds</h2>
        <p>
          Korean Lotto 6/45 draws 6 numbers from 1–45. Since there is only one pool (no separate
          bonus drum for the jackpot), the calculation is simpler:
        </p>
        <p style={{ fontFamily: 'monospace', background: 'var(--input-bg)', padding: '10px 16px', borderRadius: 6, margin: '12px 0', color: 'var(--text-primary)' }}>
          C(45, 6) = 45! / (6! × 39!) = 8,145,060
        </p>
        <p>
          The jackpot odds for Lotto 6/45 are <strong>1 in 8,145,060</strong>—significantly better
          than either US game, which is why the prizes are correspondingly smaller.
        </p>

        <h2 style={{ fontSize: '1.2em', marginTop: 28, marginBottom: 8 }}>Putting the Numbers in Context</h2>
        <p>
          Abstract large numbers are notoriously difficult for the human brain to process intuitively.
          Some comparisons that may help frame Powerball's 1-in-292-million odds:
        </p>
        <ul style={{ margin: '8px 0 0 20px', lineHeight: 1.9, color: 'var(--text-secondary)' }}>
          <li>You are roughly <strong>500 times more likely</strong> to be struck by lightning in a given year (odds: about 1 in 500,000) than to win the Powerball jackpot with one ticket.</li>
          <li>If you bought one Powerball ticket per drawing for every drawing held since the game launched in 1992, you would have bought roughly 5,000 tickets. Your cumulative odds of having won at least once would still be about 1 in 58,000.</li>
          <li>If every person on Earth (8 billion people) bought one Powerball ticket for the same drawing, you would expect about 27 jackpot winners. The other 7,999,999,973 people would have lost.</li>
          <li>At the speed of light, you could travel from Earth to the Moon in about 1.3 seconds. At one ticket per second, it would take roughly 9.3 years to buy 292 million tickets—still no guarantee of a jackpot win.</li>
        </ul>

        <h2 style={{ fontSize: '1.2em', marginTop: 28, marginBottom: 8 }}>Expected Value: What a Ticket Is Actually Worth</h2>
        <p>
          <strong>Expected value</strong> is the mathematical average outcome of a bet, calculated by
          multiplying each possible prize by its probability and summing the results. For a Powerball
          ticket at the minimum $20 million jackpot (cash value approximately $12 million before tax):
        </p>
        <p>
          The expected value of a $2 ticket is significantly less than $2—typically in the range of
          $0.30–$0.95 depending on the jackpot size. This negative expected value is true of every
          lottery ticket ever sold. The lottery is designed this way: prize pools are funded by a
          fraction of ticket revenue, with the remainder going to state budgets, retailer commissions,
          and operating costs. No matter how large the jackpot grows, the expected value of a lottery
          ticket remains below its cost.
        </p>
        <p>
          During very large jackpots (over $900 million cash value), the expected value before tax
          can technically exceed $2 for a single ticket—but this ignores the significant probability
          of sharing the jackpot with other winners, which increases dramatically as ticket sales
          surge during high-profile jackpots.
        </p>

        <h2 style={{ fontSize: '1.2em', marginTop: 28, marginBottom: 8 }}>Why "Hot" and "Cold" Numbers Don't Work</h2>
        <p>
          A common belief holds that lottery numbers that have appeared frequently in recent draws
          ("hot" numbers) are more likely to appear again, or that numbers that haven't appeared in a
          long time ("cold" numbers) are "due" to be drawn. Both beliefs are instances of the{' '}
          <strong>Gambler's Fallacy</strong>—the incorrect assumption that independent random events
          are influenced by previous outcomes.
        </p>
        <p>
          Lottery balls have no memory. A Powerball drawing selects 5 balls from 69 physical balls,
          each of which is equally likely to be drawn. The fact that ball #47 appeared in the last
          three drawings does not increase or decrease its probability of appearing in the next drawing.
          Each drawing is a fresh, statistically independent event. Historical frequency analysis of
          lottery draws consistently shows that the apparent patterns in past results are exactly what
          you would expect from random variation over a finite sample—not evidence of any systematic
          bias.
        </p>

        <h2 style={{ fontSize: '1.2em', marginTop: 28, marginBottom: 8 }}>What You Can (and Cannot) Control</h2>
        <p>
          While no strategy changes your probability of winning, there is one practical consideration
          about number selection: <strong>avoiding popular combinations can reduce the chance of
          sharing a jackpot</strong> if you do win. Popular combinations—consecutive numbers like
          1-2-3-4-5, numbers representing dates (which cap at 31), or famous "lucky" sequences—are
          chosen by many players. If one of these combinations hits, the jackpot may be split among
          dozens or hundreds of winners. Choosing less common combinations doesn't improve your odds
          of winning, but it increases your expected share if you do.
        </p>
        <p>
          Random number generation, as our{' '}
          <Link to={lp('/')}>free tool</Link> provides, produces unbiased combinations that are
          statistically no more or less likely to win than any other, while naturally avoiding the
          overrepresented patterns that human number pickers tend to favor.
        </p>

        <div className="disclaimer" style={{ marginTop: 28 }}>
          <strong>Disclaimer:</strong> For entertainment only. Lottery games have a negative expected
          value. Only spend what you can comfortably afford to lose.
        </div>

        <p style={{ marginTop: 20 }}>
          <Link to="/articles/">← Back to all articles</Link>
          {' · '}
          <Link to="/articles/how-powerball-works/">How Powerball Works</Link>
          {' · '}
          <Link to="/articles/how-rng-works/">How Random Number Generators Work</Link>
        </p>
      </article>
    </main>
  );
}
